/* eslint-disable */

import debounce from 'lodash/debounce'
import {useEffect, useMemo, useState} from 'react'

export const useCustomDataTable = ({
  columns = [],
  records = [],
  pageSize = 10,
  onResetAll = () => {},
  onRowSelect = () => {},
  handleSorting = () => {},
  setSearchArry = () => {},
  setPageIndex = () => {},
  isSelectAll = false,
  totalRecords = 0,
  initialSelectedRows = [],
  id = 'uuid',
  setSelectAll = () => {},
  defaultSortConfig = {key: null, direction: 'desc'},
}) => {
  const [dateRanges, setDateRanges] = useState({})
  const [singleDates, setSingleDates] = useState({})
  const [openDatePicker, setOpenDatePicker] = useState('')
  const [selectedRows, setSelectedRows] = useState(initialSelectedRows)
  const [page, setPage] = useState(1)
  const [sortConfig, setSortConfig] = useState(defaultSortConfig)
  const [filter, setFilter] = useState(
    columns?.reduce(
      (acc, column) => ({
        ...acc,
        [column.name_of_db_column]: '',
      }),
      {}
    )
  )

  // Track global selection state
  const [globalSelectAll, setGlobalSelectAll] = useState(false)

  useEffect(() => {
    if (isSelectAll) {
      setGlobalSelectAll(true)
      // When isSelectAll is true, replace all selections with current page
      setSelectedRows(records)
      onRowSelect(records, true)
    } else if (!isSelectAll && globalSelectAll) {
      setGlobalSelectAll(false)
      setSelectedRows([])
      onRowSelect([], false)
    }
  }, [isSelectAll, records])
  const DEBOUNCE_DELAY = 300

  // Reset all filters, sorting, and pagination
  const handleResetAll = () => {
    setSelectAll(false)
    // Reset local state first
    setDateRanges({})
    setSingleDates({})
    setOpenDatePicker('')
    setSelectedRows([])
    setGlobalSelectAll(false)
    setFilter({
      ...columns?.reduce(
        (acc, column) => ({
          ...acc,
          [column.name_of_db_column]: '',
        }),
        {}
      ),
    })

    // Reset page to 1 before calling parent reset
    setPage(1)
    setPageIndex(1) // 0-based index for page 1

    // Call the parent reset function which clears API filters
    onResetAll()

    // Notify parent about row selection change
    onRowSelect([], false)
  }
  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleSort = key => {
    let newDirection

    // If clicking a new column, start with desc
    if (sortConfig.key !== key) {
      newDirection = 'desc'
    }
    // If clicking the same column, toggle between desc and asc
    else {
      newDirection = sortConfig.direction === 'desc' ? 'asc' : 'desc'
    }

    setSortConfig({key, direction: newDirection})
    handleSorting({column: key, direction: newDirection})
  }

  const handleSelectAll = () => {
    const allCurrentPageSelected = records.every(record => {
      const recordId = record[id] ?? record.uuid
      return recordId && selectedRows.some(selected => (selected[id] ?? selected.uuid) === recordId)
    })

    if (allCurrentPageSelected) {
      // Clear all selections when deselecting
      setSelectedRows([])
      setGlobalSelectAll(false)
      onRowSelect([], false)
      setSelectAll(false)
    } else {
      // Replace all selections with current page when selecting all
      setSelectedRows(records)
      setGlobalSelectAll(true)
      onRowSelect(records, true)
      setSelectAll(true) // Notify parent component about select all action
    }
  }

  const handleRowSelect = (identifier, isUnchecking = false) => {
    if (!identifier || (isSelectAll && !isUnchecking)) return
    const selectedRow = records.find(row => {
      const rowId = row[id] ?? row.uuid
      return rowId && rowId === identifier
    })

    if (!selectedRow) return

    const isSelected = (isSelectAll ? initialSelectedRows : selectedRows).some(row => {
      const rowId = row[id] ?? row.uuid
      return rowId && rowId === identifier
    })

    let newSelection
    if (isSelected) {
      const sourceRows = isSelectAll ? initialSelectedRows : selectedRows
      newSelection = sourceRows.filter(row => {
        const rowId = row[id] ?? row.uuid
        return rowId !== identifier
      })
      setGlobalSelectAll(false)
      if (isSelectAll) setSelectAll(false)
    } else {
      newSelection = [...selectedRows, selectedRow]
    }

    setSelectedRows(newSelection)
    onRowSelect(newSelection, false)
  }
  const debouncedSetSearchArry = useMemo(
    () =>
      debounce(searchObj => {
        setSearchArry(searchObj)
      }, DEBOUNCE_DELAY),
    []
  )

  // Clean up on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchArry.cancel()
    }
  }, [debouncedSetSearchArry])
  // Handle page change - triggers new server request
  const handlePageChange = (value, nextPage) => {
    const targetPage = Number(nextPage ?? value)

    if (!Number.isInteger(targetPage) || targetPage < 1) {
      return
    }

    setPage(targetPage)
    setPageIndex(targetPage)
  }
  const handleFilterChange = e => {
    if (!e?.target) return
    const {name, value} = e.target

    if (!name) return

    // Immediate: update pagination + filters
    handlePageChange(null, 1)
    setFilter(prev => ({...prev, [name]: value ?? ''}))

    // Determine filter type
    const column = columns?.find(col => col.name_of_db_column === name)
    let searchType = 'string'

    if (column?.filterVariant) {
      switch (column.filterVariant) {
        case 'double':
          searchType = 'double'
          break
        case 'long':
          searchType = 'long'
          break
        case 'date':
          searchType = 'date'
          break
        case 'dateRange':
          searchType = 'dateRange'
          break
        case 'string':
          searchType = 'string'
          break
        default:
          searchType = 'string'
      }
    }

    // Debounced call only here
    const searchObj = {
      field: name,
      value: value ?? '',
      type: searchType,
    }

    debouncedSetSearchArry(searchObj)
  }
  const handleDateRangeChange = (dates, dateField) => {
    if (!dateField) return
    const [start, end] = dates || [null, null]

    // Always update the local date range state for UI
    setDateRanges(prev => ({
      ...prev,
      [dateField]: {startDate: start, endDate: end},
    }))

    // Reset page when date filter changes
    handlePageChange(null, 1)

    // Only proceed with API call if both dates are selected
    if (start && end) {
      // Use UTC dates to avoid timezone-related date shifting
      const startLocal = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 0, 0, 0, 0))

      const endLocal = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 23, 59, 59, 999))

      // Create search object for the API
      const searchObj = {
        field: dateField,
        value: {
          from: startLocal.toISOString(),
          to: endLocal.toISOString(),
        },
        type: 'dateRange',
      }
      // Call API with the search object
      setSearchArry(searchObj)
    } else if (start === null && end === null) {
      const searchObj = {
        field: dateField,
        value: {
          from: '',
          to: '',
        },
        type: 'dateRange',
      }
      // Call API with the search object
      setSearchArry(searchObj)
    }
    // Don't call setSearchArry if not both dates are present
  }

  const handleSingleDateChange = (date, dateField) => {
    if (!dateField) return

    // Update the local state for UI
    setSingleDates(prev => ({
      ...prev,
      [dateField]: date,
    }))

    // Reset page when date filter changes
    handlePageChange(null, 1)

    let formattedDate = ''
    if (date) {
      // Create date at midnight UTC to prevent timezone shifting
      const utcMidnight = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1, 0, 0, 0, 0)
      )
      formattedDate = utcMidnight.toISOString()
    }

    const searchObj = {
      field: dateField,
      value: formattedDate || '',
      type: 'date',
    }

    // Call API with the search object
    setSearchArry(searchObj)
  }

  // Initial data load
  useEffect(() => {
    // Remove the initial setSearchArry call as it's not needed
    // The parent component should handle initial data loading
  }, []) // Empty dependency array means this runs once on mount

  return {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    page,
    sortConfig,
    setSortConfig,
    filter,
    totalPages,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handlePageChange,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
  }
}
